import React, { useLayoutEffect, useRef } from 'react';

export default function RouteSlider({
  containerSelector,
  activeSelector,
  storageKey = 'bf_top_slider_prev',
  currentRoute = '',
  className = '',
  isActive = true,
}) {
  const sliderRef = useRef(null);

  useLayoutEffect(() => {
    if (!isActive) return undefined;

    const slider = sliderRef.current;

    // Find this slider's own container, including when Home stays mounted.
    const container = slider?.parentElement?.closest(containerSelector);

    if (!slider || !container) return undefined;

    let animationFrame = null;

    const readActiveRect = () => {
      const active = container.querySelector(activeSelector);
      if (!active) return null;

      const containerRect = container.getBoundingClientRect();
      const activeRect = active.getBoundingClientRect();

      return {
        left: Math.round(
          activeRect.left - containerRect.left - container.clientLeft
        ),
        top: Math.round(
          activeRect.top - containerRect.top - container.clientTop
        ),
        width: Math.round(activeRect.width),
        height: Math.round(activeRect.height),
        route: currentRoute,
      };
    };

    const applyRect = (rect) => {
      slider.style.width = `${rect.width}px`;
      slider.style.height = `${rect.height}px`;
      slider.style.transform =
        `translate(${rect.left}px, ${rect.top}px)`;
    };

    const saveRect = (rect) => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(rect));
      } catch {}
    };

    const target = readActiveRect();
    if (!target) return undefined;

    let previous = null;

    try {
      previous = JSON.parse(
        sessionStorage.getItem(storageKey) || 'null'
      );
    } catch {}

    const hasPreviousRect =
      previous &&
      ['left', 'top', 'width', 'height'].every(
        (key) => Number.isFinite(previous[key])
      );

    const shouldSlide =
      hasPreviousRect &&
      previous.route &&
      previous.route !== currentRoute;

    // Establish the starting position before the browser paints.
    slider.style.transition = 'none';
    applyRect(shouldSlide ? previous : target);
    void slider.offsetHeight;
    slider.style.transition = '';

    // Store the final position, not a partly animated position.
    saveRect(target);

    if (shouldSlide) {
      animationFrame = requestAnimationFrame(() => {
        applyRect(target);
        animationFrame = null;
      });
    }

    const handleResize = () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
      }

      const nextRect = readActiveRect();
      if (!nextRect) return;

      slider.style.transition = 'none';
      applyRect(nextRect);
      void slider.offsetHeight;
      slider.style.transition = '';

      saveRect(nextRect);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame);
      }

      window.removeEventListener('resize', handleResize);
    };
  }, [
    containerSelector,
    activeSelector,
    storageKey,
    currentRoute,
    isActive,
  ]);

  return (
    <div
      ref={sliderRef}
      className={`route-slider route-slider--pill ${className}`.trim()}
      aria-hidden="true"
    />
  );
}
